//
// # RectEntity
//

import { graphics, Point, Bbox, LineSegment } from "GenAlphaModel";
import { EntityKind } from "../entity";
import { MousePos } from "../mousepos";
import { DrawTo } from "../draw";
import { ShapeBase } from "../tree";

const EDGE_HIT_TOLERANCE = 5;

export class RectEntity extends ShapeBase<graphics.Rectangle> {
  readonly entityKind = EntityKind.Rect;

  override redraw = (to: DrawTo): unknown => {
    return to.rect(this.data, this.style);
  };

  get corners(): [RectCorner, RectCorner, RectCorner, RectCorner] {
    const { bbox } = this.data;
    const opposites = [
      [bbox.min, bbox.max],
      [
        { x: bbox.min.x, y: bbox.max.y },
        { x: bbox.max.x, y: bbox.min.y },
      ],
    ];
    return [
      new RectCorner(this, opposites[0][0], opposites[0][1]),
      new RectCorner(this, opposites[0][1], opposites[0][0]),
      new RectCorner(this, opposites[1][0], opposites[1][1]),
      new RectCorner(this, opposites[1][1], opposites[1][0]),
    ];
  }

  get body(): RectBody {
    return new RectBody(this);
  }

  // Current center of the rectangle.
  get loc(): Point {
    return Bbox.sizeAndCenter(this.data.bbox).center;
  }

  // Translate the rectangle to a new center location.
  updateLoc = (loc: Point): unknown => {
    const { size } = Bbox.sizeAndCenter(this.data.bbox);
    return this.update({ bbox: Bbox.fromSizeAndCenter(size, loc) });
  };

  // Hit test against the four edges (for whole-shape movement).
  edgeHitTest = (mousePos: MousePos): boolean => {
    const { bbox } = this.data;
    const corners = [
      bbox.min,
      { x: bbox.max.x, y: bbox.min.y },
      bbox.max,
      { x: bbox.min.x, y: bbox.max.y },
    ];
    for (let i = 0; i < corners.length; i++) {
      const next = corners[(i + 1) % corners.length];
      const seg: LineSegment = { ends: [corners[i], next] };
      if (LineSegment.closest(mousePos.sch, seg).dist < EDGE_HIT_TOLERANCE) {
        return true;
      }
    }
    return false;
  };
}

export class RectBody {
  readonly entityKind = EntityKind.RectBody;
  constructor(readonly parent: RectEntity) {}
  hitTest = (mousePos: MousePos): boolean => false; // FIXME!
  redraw = (to: DrawTo) => this.parent.redraw(to);
}

export class RectCorner {
  readonly entityKind = EntityKind.RectCorner;

  constructor(
    public parent: RectEntity,
    public loc: Point,
    public opposite: Point
  ) {}

  hitTest = (mousePos: MousePos): boolean => {
    return Point.dist(mousePos.sch, this.loc) < 5; // FIXME actually setting this tolerance
  };

  update = (data: { loc: Point }) => {
    const { loc } = data;
    const rect = { bbox: Bbox.fromPoints(loc, this.opposite) };
    this.parent.update(rect);
    this.loc = loc;
  };
  redraw = (to: DrawTo) => this.parent.redraw(to);
  highlight = (state: boolean) => this.parent.highlight(state);
}
